import React, { useState, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';

const fmtAmount = (val, currency) => {
  const n = parseFloat(val) || 0;
  if (currency === 'USD') return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const LoadEstimateModal = ({ onClose, onLoad, currency }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`/api/estimates?customer=${encodeURIComponent(q)}&limit=50`, { credentials: 'include' });
      const data = await res.json();
      let estimates = data.success ? data.estimates : [];
      if (currency) estimates = estimates.filter(e => e.currency === currency);
      setResults(estimates);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, [currency]);

  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 400);
    return () => clearTimeout(t);
  }, [search, doSearch]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Search size={16} /> Load Saved Estimate
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-4">
          <input
            autoFocus
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by customer name or phone..."
            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>
        <div className="max-h-80 overflow-y-auto px-4 pb-4">
          {loading && <div className="text-center text-gray-500 text-xs py-4">Searching...</div>}
          {!loading && results.length === 0 && search.length >= 2 && (
            <div className="text-center text-gray-400 text-xs py-4">No estimates found</div>
          )}
          {results.map(est => (
            <div
              key={est.id}
              onClick={() => onLoad(est)}
              className="flex items-center justify-between p-3 border rounded mb-2 cursor-pointer hover:bg-amber-50 hover:border-amber-300 transition-colors"
            >
              <div>
                <div className="text-sm font-medium">{est.customer_name}</div>
                <div className="text-xs text-gray-500">
                  {est.estimate_number} · {est.item_code || '—'} · {est.jewelry_name || '—'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-amber-700">
                  {fmtAmount(est.currency === 'USD' ? (est.grand_total_usd ?? est.grand_total) : est.grand_total, est.currency)}
                </div>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${est.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {est.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoadEstimateModal;
